import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { RevealText, Reveal } from "@/components/site/Reveal";
import { BrandMarquee } from "@/components/site/Marquee";
import { IMAGES, SERVICES } from "@/lib/assets";

const PageHead = ({ kicker, lines, sub }) => (
  <section className="relative pt-40 pb-20 mx-auto max-w-[1600px] px-5 sm:px-8">
    <p className="font-mono text-xs uppercase tracking-[0.3em] text-primary mb-6">{kicker}</p>
    <h1 className="font-display uppercase tracking-tighter leading-[0.85] text-[13vw] sm:text-[10vw] lg:text-[8vw]">
      <RevealText lines={lines} testid="page-title" />
    </h1>
    {sub && <Reveal delay={0.4}><p className="mt-8 max-w-xl text-white/70 text-lg">{sub}</p></Reveal>}
  </section>
);

export default function Services() {
  return (
    <div data-testid="services-page">
      <PageHead
        kicker="/ Explore our services"
        lines={["Every", "mode."]}
        sub="At Freight IQ, we offer a full suite of international freight forwarding services designed to keep your cargo moving — across borders, oceans, and time zones. Whether you need air freight for speed or ocean freight for economy, our team delivers with precision, transparency and care."
      />

      <BrandMarquee />

      <div className="mx-auto max-w-[1600px] px-5 sm:px-8 py-24 space-y-28">
        {SERVICES.map((s, i) => (
          <section key={s.key} data-testid={`service-${s.key}`} className="grid lg:grid-cols-2 gap-12 items-center">
            <Reveal className={i % 2 ? "lg:order-2" : ""}>
              <Link to={`/services/${s.key}`} className="relative h-[420px] block overflow-hidden border border-white/10 group hover:border-primary/60 transition-colors duration-500">
                <img src={s.img} alt={s.title} className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                <span className="absolute top-6 left-6 font-display text-6xl stroke-text">{s.num}</span>
              </Link>
            </Reveal>
            <Reveal delay={0.1} className={i % 2 ? "lg:order-1" : ""}>
              <span className="font-mono text-[10px] uppercase tracking-widest text-primary border border-primary/40 px-3 py-1">{s.tag}</span>
              <h2 className="font-display text-4xl sm:text-5xl uppercase tracking-tighter mt-6">{s.title}</h2>
              <p className="text-white/60 text-lg mt-4">{s.desc}</p>
              <ul className="mt-8 grid sm:grid-cols-2 gap-3">
                {s.points.map((p) => (
                  <li key={p} className="flex items-center gap-3 text-white/80">
                    <Check size={16} className="text-primary shrink-0" /> {p}
                  </li>
                ))}
              </ul>
              <Link to={`/services/${s.key}`} data-testid={`service-readmore-${s.key}`} className="inline-flex items-center gap-2 mt-8 font-mono text-xs uppercase tracking-widest text-white/70 hover:text-primary transition-colors">
                Read More <ArrowUpRight size={14} />
              </Link>
            </Reveal>
          </section>
        ))}
      </div>

      <section className="border-y border-white/10 bg-[#080808]">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 py-24 grid lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-6">
            <p className="font-mono text-xs uppercase tracking-widest text-primary mb-4">/ Global Transport and Logistics</p>
            <h2 className="font-display text-3xl sm:text-4xl uppercase tracking-tighter leading-tight">Providing exclusive logistics & transport services for you</h2>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-6 space-y-5 text-white/60 text-lg">
            <p className="text-white/80 italic">"With years of experience in logistics and transportation, we pride ourselves on delivering customized solutions to businesses."</p>
            <p>From freight management to supply chain optimization, we are committed to providing the best services to ensure your success — with precision, transparency, and care at every step.</p>
            <Link to="/contact" className="inline-flex items-center gap-2 pt-2 font-mono text-xs uppercase tracking-widest text-white/70 hover:text-primary transition-colors">
              Explore More <ArrowUpRight size={14} />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="relative h-[50vh] overflow-hidden">
        <img src={IMAGES.airTarmac} alt="Air cargo tarmac" className="absolute inset-0 h-full w-full object-cover opacity-55" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] to-transparent" />
        <div className="relative z-10 h-full mx-auto max-w-[1600px] px-5 sm:px-8 flex items-center">
          <Reveal>
            <h2 className="font-display text-4xl sm:text-6xl uppercase tracking-tighter max-w-3xl">Need a multi-modal solution?</h2>
            <Link to="/contact" className="inline-flex items-center gap-2 mt-8 hover-fill px-8 py-4 font-mono text-xs uppercase tracking-widest">
              Talk to our desk <ArrowUpRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
